import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  X,
  Mail,
  KeyRound,
  User,
  ArrowRight,
  ShieldCheck,
  Sparkles,
  Gift,
  Wallet,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  RefreshCw,
  Clock,
  Edit3,
} from 'lucide-react';
import { toast } from 'sonner';
import AgreementCheckbox from './AgreementCheckbox';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * CustomerAuthModal — premium dark-mode modal for the global "Customer Login"
 * entry point. Mirrors RestaurantAuthModal so the brand experience is coherent.
 *
 * - Sign in is the DEFAULT tab (per UX rule — returning users dominate clicks).
 * - Magic-link (passwordless) is offered below the password form AND via
 *   the "Forgot password?" link.
 * - Signup is the secondary tab.
 * - Mobile = full screen; desktop = two-pane card (image left, form right).
 */

const benefits = [
  { icon: Gift, label: 'Earn rewards on every order' },
  { icon: Wallet, label: 'Gift credits redeemable at any restaurant' },
  { icon: ShieldCheck, label: 'Lower prices · 0% delivery markup' },
];

const inputBase =
  'w-full pl-11 pr-4 py-3.5 rounded-xl bg-white/[0.04] border border-white/10 ' +
  'text-white placeholder-white/45 text-[15px] ' +
  'focus:outline-none focus:border-yellow-500/60 focus:bg-white/[0.06] transition-colors';

const SignInForm = ({ onSwitchToSignup, onClose }) => {
  const [view, setView] = useState('password');
  const [form, setForm] = useState({ email: '', password: '' });
  const [submitting, setSubmitting] = useState(false);

  // Magic-link recovery state (resend + delivery status + countdown).
  // Status: 'idle' | 'sending' | 'sent' | 'failed' | 'resending'
  const [magicStatus, setMagicStatus] = useState('idle');
  const [resendIn, setResendIn] = useState(0);
  const [resendCount, setResendCount] = useState(0);

  // Fire-and-forget telemetry — best-effort, never blocks the UI.
  const trackMagicEvent = (event, metadata = {}) => {
    try {
      axios
        .post(`${API}/api/telemetry/event`, {
          event,
          metadata: {
            email_hash: (form.email || '').trim().toLowerCase().slice(0, 6) + '…',
            ...metadata,
          },
        })
        .catch(() => {});
    } catch {
      /* ignore */
    }
  };

  // Cooldown ticker — decrements every second while resendIn > 0.
  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setInterval(() => {
      setResendIn((n) => (n > 0 ? n - 1 : 0));
    }, 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.password.trim()) {
      toast.error('Enter your email and password.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/customers/auth/login`, {
        email: form.email.trim().toLowerCase(),
        password: form.password,
      });
      if (res.data?.token) {
        localStorage.setItem('nofee_customer_token', res.data.token);
        toast.success('Welcome back!');
        onClose?.();
        setTimeout(() => {
          window.location.href = '/account';
        }, 350);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid email or password.');
    } finally {
      setSubmitting(false);
    }
  };

  // Send (or re-send) the magic link. Sets visible delivery status so the
  // user always knows what happened.
  const sendMagicLink = async ({ resend = false } = {}) => {
    const email = form.email.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      toast.error('Enter a valid email to receive your sign-in link.');
      return;
    }
    setMagicStatus(resend ? 'resending' : 'sending');
    trackMagicEvent(resend ? 'magic_link_resend_requested' : 'magic_link_sent', {
      attempt: resendCount + (resend ? 1 : 0) + 1,
    });
    try {
      await axios.post(`${API}/api/customers/auth/magic-link`, { email });
      setMagicStatus('sent');
      setView('magic-sent');
      // 30s cooldown to prevent abuse — also covers the case where the
      // user already submitted via the password-tab "Forgot password?" flow.
      setResendIn(30);
      if (resend) setResendCount((n) => n + 1);
    } catch (err) {
      setMagicStatus('failed');
      setView('magic-sent'); // keep them on the recovery view, but show error UI
      toast.error(err?.response?.data?.detail || 'Could not send your sign-in link.');
    }
  };

  const submitMagicLink = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await sendMagicLink({ resend: false });
    } finally {
      setSubmitting(false);
    }
  };

  if (view === 'magic-sent') {
    const isFailed = magicStatus === 'failed';
    const isResending = magicStatus === 'resending';
    const canResend = resendIn === 0 && !isResending;

    return (
      <div className="py-2" data-testid="cust-magic-sent">
        {/* Delivery status banner */}
        {isFailed ? (
          <div
            className="mx-auto w-14 h-14 rounded-2xl bg-rose-500/[0.10] border border-rose-500/30 flex items-center justify-center mb-4"
            data-testid="cust-magic-status-icon-failed"
          >
            <AlertTriangle className="w-6 h-6 text-rose-300" strokeWidth={1.75} />
          </div>
        ) : (
          <div
            className="mx-auto w-14 h-14 rounded-2xl bg-emerald-500/[0.10] border border-emerald-500/30 flex items-center justify-center mb-4"
            data-testid="cust-magic-status-icon-sent"
          >
            <CheckCircle2 className="w-6 h-6 text-emerald-300" strokeWidth={1.75} />
          </div>
        )}

        <h3
          className="text-center text-white text-[17px] font-bold tracking-tight"
          data-testid="cust-magic-status-headline"
        >
          {isFailed ? 'Unable to send email' : 'Login link sent successfully'}
        </h3>
        <p className="mt-2 text-center text-[13px] text-white/65 leading-relaxed">
          {isFailed ? (
            <>Please try again, or correct your email below.</>
          ) : (
            <>
              We sent a one-click sign-in link to{' '}
              <strong className="text-white">{form.email.trim().toLowerCase()}</strong>. The link
              expires in 15 minutes.
            </>
          )}
        </p>

        {/* Resend block */}
        <div
          className="mt-5 p-4 rounded-xl border border-white/8 bg-white/[0.02]"
          data-testid="cust-magic-resend-block"
        >
          <p className="text-[12.5px] text-white/65 font-medium">Didn't receive the email?</p>
          <button
            type="button"
            onClick={() => sendMagicLink({ resend: true })}
            disabled={!canResend}
            data-testid="cust-magic-resend-btn"
            className={`mt-2.5 w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl text-[13.5px] font-bold transition-all ${
              canResend
                ? 'bg-gradient-to-b from-yellow-300 to-yellow-500 text-black hover:from-yellow-200 hover:to-yellow-400 shadow-[0_10px_25px_-10px_rgba(250,204,21,0.55)]'
                : 'border border-white/12 bg-white/[0.02] text-white/45 cursor-not-allowed'
            }`}
            aria-live="polite"
          >
            {isResending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Sending…
              </>
            ) : canResend ? (
              <>
                <RefreshCw className="w-4 h-4" /> Resend email
              </>
            ) : (
              <>
                <Clock className="w-4 h-4" />
                <span data-testid="cust-magic-resend-countdown">
                  Resend available in {resendIn}s
                </span>
              </>
            )}
          </button>
          {resendCount > 0 && !isResending && (
            <p
              className="mt-2 text-center text-[11.5px] text-white/40"
              data-testid="cust-magic-resend-count"
            >
              Sent {resendCount + 1} times in this session
            </p>
          )}
        </div>

        {/* Spam reminder */}
        <p
          className="mt-4 text-center text-[12px] text-white/50 leading-relaxed"
          data-testid="cust-magic-spam-reminder"
        >
          Please check your Spam/Junk folder if you do not see the email within a few minutes.
        </p>

        {/* Change email + back-to-password actions */}
        <div className="mt-5 grid grid-cols-2 gap-2.5">
          <button
            type="button"
            onClick={() => {
              setMagicStatus('idle');
              setResendIn(0);
              setResendCount(0);
              setView('magic-request');
            }}
            data-testid="cust-magic-change-email"
            className="inline-flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-white/12 hover:border-yellow-500/40 bg-white/[0.02] hover:bg-white/[0.04] text-white/85 hover:text-white text-[12.5px] font-bold transition-all"
          >
            <Edit3 className="w-3.5 h-3.5" /> Change email
          </button>
          <button
            type="button"
            onClick={() => {
              setMagicStatus('idle');
              setResendIn(0);
              setResendCount(0);
              setView('password');
            }}
            data-testid="cust-magic-back-to-password"
            className="inline-flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-white/12 hover:border-yellow-500/40 bg-white/[0.02] hover:bg-white/[0.04] text-white/85 hover:text-white text-[12.5px] font-bold transition-all"
          >
            <KeyRound className="w-3.5 h-3.5" /> Use password
          </button>
        </div>
      </div>
    );
  }

  if (view === 'magic-request') {
    return (
      <form onSubmit={submitMagicLink} data-testid="cust-magic-form" className="space-y-3">
        <div className="relative">
          <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-yellow-400/80" />
          <input
            type="email"
            placeholder="Email address"
            value={form.email}
            onChange={onChange('email')}
            data-testid="cust-magic-input"
            className={inputBase}
            required
            autoFocus
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          data-testid="cust-magic-submit"
          className="w-full py-3.5 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 text-black font-extrabold text-[15px] tracking-tight hover:from-yellow-200 hover:to-yellow-400 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-[0_15px_40px_-15px_rgba(250,204,21,0.55)]"
        >
          {submitting ? 'Sending…' : 'Email me a sign-in link'}
        </button>
        <p className="text-center text-[12.5px] text-white/55">
          Prefer your password?{' '}
          <button
            type="button"
            onClick={() => setView('password')}
            className="text-yellow-300 hover:text-yellow-200 font-bold"
          >
            Sign in with password
          </button>
        </p>
      </form>
    );
  }

  return (
    <form onSubmit={submit} data-testid="cust-login-form" className="space-y-3">
      <div className="relative">
        <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-yellow-400/80" />
        <input
          type="email"
          placeholder="Email address"
          value={form.email}
          onChange={onChange('email')}
          data-testid="cust-login-email"
          className={inputBase}
          required
          autoFocus
        />
      </div>
      <div className="relative">
        <KeyRound className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-yellow-400/80" />
        <input
          type="password"
          placeholder="Password"
          value={form.password}
          onChange={onChange('password')}
          data-testid="cust-login-password"
          className={inputBase}
          required
        />
      </div>

      <div className="flex items-center justify-end">
        <button
          type="button"
          onClick={() => setView('magic-request')}
          data-testid="cust-login-forgot"
          className="text-[12px] text-yellow-300/85 hover:text-yellow-200 font-medium transition-colors"
        >
          Forgot password?
        </button>
      </div>

      <button
        type="submit"
        disabled={submitting}
        data-testid="cust-login-submit"
        className="mt-2 w-full py-3.5 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 text-black font-extrabold text-[15px] tracking-tight hover:from-yellow-200 hover:to-yellow-400 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-[0_15px_40px_-15px_rgba(250,204,21,0.55)]"
      >
        {submitting ? 'Signing in…' : 'Sign in'}
      </button>

      <div className="relative my-2 flex items-center gap-3" aria-hidden="true">
        <span className="flex-1 h-px bg-white/10" />
        <span className="text-[10px] text-white/40 uppercase tracking-[0.2em] font-bold">or</span>
        <span className="flex-1 h-px bg-white/10" />
      </div>
      <button
        type="button"
        onClick={() => setView('magic-request')}
        data-testid="cust-open-magic"
        className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl border border-white/12 hover:border-yellow-500/40 bg-white/[0.02] hover:bg-white/[0.04] text-white/85 hover:text-white font-bold text-[13.5px] transition-all"
      >
        <Mail className="w-4 h-4 text-yellow-400/85" />
        Email me a sign-in link
      </button>

      <p className="text-center text-[12.5px] text-white/55 pt-2">
        New to NoFee?{' '}
        <button
          type="button"
          onClick={onSwitchToSignup}
          data-testid="cust-switch-to-signup"
          className="text-yellow-300 hover:text-yellow-200 font-bold"
        >
          Create your free account
        </button>
      </p>
    </form>
  );
};

const CreateAccountForm = ({ onSwitchToSignIn, onClose }) => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    zip_code: '',
  });
  const [submitting, setSubmitting] = useState(false);
  // iter125 — Customer Agreement + Rewards Program Terms must both be
  // accepted before account creation. The customer account is auto-enrolled
  // into the Rewards Program, so the Rewards Terms acceptance is captured
  // here, at the moment of enrollment, alongside the Customer Agreement.
  const [acceptedCustomer, setAcceptedCustomer] = useState(false);
  const [acceptedRewards, setAcceptedRewards] = useState(false);

  const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.password.trim() || !form.zip_code.trim()) {
      toast.error('Please fill in all fields.');
      return;
    }
    if (form.password.length < 6) {
      toast.error('Password must be at least 6 characters.');
      return;
    }
    // iter125 — Block until both consents are recorded.
    if (!acceptedCustomer) {
      toast.error('Please review and accept the Customer Agreement to continue.');
      return;
    }
    if (!acceptedRewards) {
      toast.error('Please review and accept the Rewards Program Terms to continue.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/customers/auth/register`, {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        zip_code: form.zip_code.trim(),
      });
      if (res.data?.token) {
        localStorage.setItem('nofee_customer_token', res.data.token);
        toast.success('Welcome to NoFee!');
        onClose?.();
        setTimeout(() => {
          // Send brand-new customers STRAIGHT to /account/settings so they
          // complete address, phone & state BEFORE trying to place an order.
          // ?welcome=1 triggers the yellow "Complete your profile" banner.
          window.location.href = '/account/settings?welcome=1';
        }, 350);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create your account.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="cust-signup-form" className="space-y-3">
      <div className="relative">
        <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-yellow-400/80" />
        <input
          type="text"
          placeholder="Your name"
          value={form.name}
          onChange={onChange('name')}
          data-testid="cust-signup-name"
          className={inputBase}
          required
          autoFocus
        />
      </div>
      <div className="relative">
        <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-yellow-400/80" />
        <input
          type="email"
          placeholder="Email address"
          value={form.email}
          onChange={onChange('email')}
          data-testid="cust-signup-email"
          className={inputBase}
          required
        />
      </div>
      <div className="relative">
        <KeyRound className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-yellow-400/80" />
        <input
          type="password"
          placeholder="Create a password (min. 6 chars)"
          value={form.password}
          onChange={onChange('password')}
          data-testid="cust-signup-password"
          className={inputBase}
          required
          minLength={6}
        />
      </div>
      <div className="relative">
        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-yellow-400/80 text-[13px] font-bold tracking-wide">
          ZIP
        </span>
        <input
          type="text"
          placeholder="Delivery ZIP code"
          value={form.zip_code}
          onChange={onChange('zip_code')}
          data-testid="cust-signup-zip"
          className="w-full pl-14 pr-4 py-3.5 rounded-xl bg-white/[0.04] border border-white/10 text-white placeholder-white/45 text-[15px] focus:outline-none focus:border-yellow-500/60 focus:bg-white/[0.06] transition-colors"
          required
        />
      </div>

      <button
        type="submit"
        disabled={submitting}
        data-testid="cust-signup-submit"
        className="mt-2 w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 text-black font-extrabold text-[15px] tracking-tight hover:from-yellow-200 hover:to-yellow-400 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-[0_15px_40px_-15px_rgba(250,204,21,0.55)]"
      >
        <Sparkles className="w-4 h-4" />
        {submitting ? 'Creating your account…' : 'Create my free account'}
        <ArrowRight className="w-4 h-4" />
      </button>

      {/* iter125 — Customer Agreement + Rewards Program Terms consents. */}
      <div className="space-y-2 pt-1">
        <AgreementCheckbox
          checked={acceptedCustomer}
          onChange={setAcceptedCustomer}
          agreementLabel="Customer Agreement"
          agreementHref="/customer-agreement"
          testid="cust-modal-customer-agreement"
        />
        <AgreementCheckbox
          checked={acceptedRewards}
          onChange={setAcceptedRewards}
          agreementLabel="Rewards Program Terms"
          agreementHref="/rewards-terms"
          testid="cust-modal-rewards-terms"
        />
      </div>

      <p className="text-center text-[11.5px] text-white/40 px-2">
        Free forever. Earn rewards on every order.
      </p>
      <p className="text-center text-[12.5px] text-white/55">
        Already a member?{' '}
        <button
          type="button"
          onClick={onSwitchToSignIn}
          data-testid="cust-switch-to-signin"
          className="text-yellow-300 hover:text-yellow-200 font-bold"
        >
          Sign in
        </button>
      </p>
    </form>
  );
};

const CustomerAuthModal = ({ open, initialMode = 'login', onClose }) => {
  const [mode, setMode] = useState(initialMode);

  useEffect(() => {
    if (open) setMode(initialMode);
  }, [open, initialMode]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="customer-auth-modal"
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
      style={{ width: '100vw', height: '100dvh' }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-5xl overflow-y-auto rounded-3xl bg-gradient-to-br from-zinc-950 via-black to-zinc-950 border border-yellow-500/15 shadow-[0_50px_120px_-30px_rgba(0,0,0,0.95)] grid grid-cols-1 md:grid-cols-2"
        style={{ maxHeight: 'calc(100dvh - 32px)' }}
      >
        {/* LEFT — visual / value pitch */}
        <div className="hidden md:flex relative bg-black overflow-hidden">
          <img
            src="/brand/customer_delivery.png"
            alt="NoFee customer rewards"
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover opacity-65"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/40 to-black/80" />
          <div className="relative z-10 flex flex-col justify-between p-9 lg:p-11 text-white">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-yellow-500/30 bg-yellow-500/[0.08] backdrop-blur-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-[10.5px] uppercase tracking-[0.22em] text-yellow-300 font-bold">
                  Welcome to NoFee
                </span>
              </div>
              <h2
                data-testid="cust-auth-marketing-headline"
                className="mt-5 font-['Outfit',_Inter,_sans-serif] text-[34px] lg:text-[42px] font-semibold tracking-[-0.03em] leading-[1.05] text-white max-w-sm"
              >
                Pay less for your{' '}
                <span className="bg-gradient-to-r from-yellow-300 to-amber-500 bg-clip-text text-transparent">
                  food.
                </span>
              </h2>
              <p
                data-testid="cust-auth-marketing-subline"
                className="mt-4 text-[14px] text-white/75 max-w-md leading-relaxed"
              >
                Order directly through NoFee and enjoy better prices from your favorite local
                restaurants.
              </p>
            </div>

            <ul className="space-y-3 mt-8">
              {benefits.map(({ icon: Icon, label }) => (
                <li key={label} className="flex items-center gap-3 text-[13.5px] text-white/85">
                  <span className="inline-flex w-8 h-8 rounded-lg border border-yellow-500/30 bg-yellow-500/[0.10] items-center justify-center shrink-0">
                    <Icon className="w-4 h-4 text-yellow-300" strokeWidth={1.7} />
                  </span>
                  {label}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* RIGHT — auth card */}
        <div className="relative p-6 sm:p-9 lg:p-11">
          <button
            type="button"
            onClick={onClose}
            data-testid="cust-auth-modal-close"
            aria-label="Close"
            className="absolute top-4 right-4 w-10 h-10 rounded-full border border-white/10 hover:border-yellow-500/40 hover:bg-white/[0.04] flex items-center justify-center text-white/60 hover:text-yellow-300 transition-colors"
          >
            <X className="w-4.5 h-4.5" />
          </button>

          <div className="md:hidden inline-flex items-center gap-2 px-3 py-1.5 mb-4 rounded-full border border-yellow-500/30 bg-yellow-500/[0.08]">
            <Gift className="w-3.5 h-3.5 text-yellow-300" />
            <span className="text-[10.5px] uppercase tracking-[0.22em] text-yellow-300 font-bold">
              NoFee Rewards
            </span>
          </div>

          <h3
            className="font-['Outfit',_Inter,_sans-serif] text-[26px] sm:text-[28px] font-semibold tracking-[-0.025em] text-white"
            data-testid="cust-auth-headline"
          >
            {mode === 'login' ? 'Welcome back' : 'Create your free account'}
          </h3>
          <p className="mt-2 text-[13.5px] text-white/55 max-w-sm leading-relaxed">
            {mode === 'login'
              ? 'Sign in to access your NoFee account, saved restaurants and rewards.'
              : 'Free forever. Earn rewards on every order, starting today.'}
          </p>

          <div
            className="grid grid-cols-2 p-1 mt-6 rounded-2xl bg-white/[0.04] border border-white/8"
            role="tablist"
            data-testid="cust-auth-tabs"
          >
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'login'}
              onClick={() => setMode('login')}
              data-testid="cust-auth-tab-login"
              className={`py-2.5 rounded-xl text-[13px] font-bold transition-all ${
                mode === 'login'
                  ? 'bg-gradient-to-b from-yellow-300 to-yellow-500 text-black shadow-[0_8px_20px_-8px_rgba(250,204,21,0.55)]'
                  : 'text-white/65 hover:text-white'
              }`}
            >
              Sign in
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'signup'}
              onClick={() => setMode('signup')}
              data-testid="cust-auth-tab-signup"
              className={`py-2.5 rounded-xl text-[13px] font-bold transition-all ${
                mode === 'signup'
                  ? 'bg-gradient-to-b from-yellow-300 to-yellow-500 text-black shadow-[0_8px_20px_-8px_rgba(250,204,21,0.55)]'
                  : 'text-white/65 hover:text-white'
              }`}
            >
              Create account
            </button>
          </div>

          <div className="mt-6">
            {mode === 'login' ? (
              <SignInForm
                onSwitchToSignup={() => setMode('signup')}
                onClose={onClose}
              />
            ) : (
              <CreateAccountForm
                onSwitchToSignIn={() => setMode('login')}
                onClose={onClose}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerAuthModal;
